// src/pages/VoucherDetail.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaBan,
  FaCheckCircle,
  FaClock,
  FaDownload,
  FaExclamationTriangle,
  FaPrint,
  FaQrcode,
  FaShieldAlt,
  FaTicketAlt,
  FaTimesCircle,
} from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  displayVoucherStatus,
  fetchVoucherByToken,
  getVoucherEffectiveStatus,
  getVoucherQrUrl,
  getVoucherRedemptionInfo,
  getVoucherStatusBadgeClass,
} from '../lib/vouchers';
import { moneyCad, dateOnly, dateTime } from '../lib/travelerData';
import { useTransactionI18n } from '../lib/transactionI18n';
import { downloadVoucherPdf } from '../lib/pdfDocuments';

type LoadState = 'loading' | 'ready' | 'error';

const statusIcon = (status: string) => {
  if (status === 'active') return <FaCheckCircle />;
  if (status === 'redeemed') return <FaCheckCircle />;
  if (status === 'expired') return <FaClock />;
  if (status === 'cancelled' || status === 'canceled') return <FaTimesCircle />;
  return <FaTicketAlt />;
};

const VoucherDetail: React.FC = () => {
  const { token = '' } = useParams();
  const { tx, voucherStatus, language } = useTransactionI18n();
  const [state, setState] = useState<LoadState>('loading');
  const [voucher, setVoucher] = useState<any>(null);
  const [error, setError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setState('loading');
      setError('');

      try {
        const { data, error: fetchError } = await fetchVoucherByToken(token);

        if (fetchError || !data) {
          setError(tx('voucherNotFound'));
          setState('error');
          return;
        }

        setVoucher(data);
        setState('ready');
      } catch (err) {
        console.error(err);
        setError(tx('voucherTechError'));
        setState('error');
      }
    };

    load();
  }, [token]);

  const qrUrl = useMemo(() => (voucher?.secure_token ? getVoucherQrUrl(voucher.secure_token) : ''), [voucher]);
  const effectiveStatus = getVoucherEffectiveStatus(voucher);
  const status = voucherStatus(effectiveStatus);
  const statusClass = getVoucherStatusBadgeClass(effectiveStatus);
  const isActive = effectiveStatus === 'active';
  const isRedeemed = effectiveStatus === 'redeemed';
  const isExpired = effectiveStatus === 'expired';
  const isCancelled = effectiveStatus === 'cancelled' || effectiveStatus === 'canceled';
  const redemption = getVoucherRedemptionInfo(voucher);

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <Header />

      <main className="pt-28 pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Link to="/mes-achats" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 font-bold mb-6">
            <FaArrowLeft /> {tx('backToPurchases')}
          </Link>

          {state === 'loading' ? (
            <div className="bg-white border border-slate-100 rounded-3xl p-12 text-center shadow-sm">
              <div className="w-12 h-12 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
              <p className="font-bold text-slate-500">{tx('loadingVoucher')}</p>
            </div>
          ) : state === 'error' ? (
            <div className="bg-white border border-red-100 rounded-3xl p-12 text-center shadow-sm">
              <FaExclamationTriangle className="mx-auto text-5xl text-red-400 mb-5" />
              <h1 className="text-2xl font-black text-slate-900 mb-2">{tx('voucherUnavailable')}</h1>
              <p className="text-slate-500 mb-8">{error}</p>
              <Link to="/mes-achats" className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-black">
                {tx('returnToPurchases')}
              </Link>
            </div>
          ) : (
            <section className="bg-white rounded-[2rem] border border-slate-100 overflow-hidden shadow-xl">
              <div className={`p-8 text-white text-center relative ${isActive ? 'bg-gradient-to-br from-slate-950 via-blue-950 to-blue-600' : 'bg-gradient-to-br from-slate-950 via-slate-800 to-slate-600'}`}>
                <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-2 text-xs font-black uppercase tracking-widest mb-5">
                  <FaTicketAlt /> {tx('officialVoucher')}
                </div>
                <h1 className="text-3xl md:text-4xl font-black leading-tight">{voucher.title || voucher.item?.title || tx('experienceForfeo')}</h1>
                <p className="text-blue-100 mt-3">
                  {isActive
                    ? tx('voucherActiveInstructions')
                    : tx('voucherStatusUpdated')}
                </p>
              </div>

              <div className="p-8">
                <div className="grid md:grid-cols-[260px_1fr] gap-8 items-start">
                  <div className="rounded-3xl border border-slate-200 p-5 bg-slate-50 text-center relative overflow-hidden">
                    {qrUrl ? (
                      <img
                        src={qrUrl}
                        alt="QR Code Voucher Forfeo"
                        className={`w-full max-w-[220px] mx-auto rounded-2xl bg-white p-3 ${isActive ? '' : 'opacity-25 grayscale'}`}
                      />
                    ) : (
                      <div className="w-full aspect-square rounded-2xl bg-white flex items-center justify-center text-slate-300">
                        <FaQrcode className="text-6xl" />
                      </div>
                    )}

                    {!isActive && (
                      <div className="absolute inset-5 rounded-2xl bg-slate-950/75 text-white flex flex-col items-center justify-center p-4 text-center">
                        <div className="text-4xl mb-3">{statusIcon(effectiveStatus)}</div>
                        <div className="text-xs font-black uppercase tracking-widest">{status}</div>
                        <div className="text-[11px] text-white/70 mt-2 leading-5">
                          {isRedeemed && tx('qrCannotBeUsed')}
                          {isExpired && tx('usagePeriodExpired')}
                          {isCancelled && tx('voucherWasCancelled')}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 font-mono text-sm font-black tracking-widest text-slate-800 break-all">
                      {voucher.voucher_code}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-black uppercase tracking-widest ${statusClass}`}>
                      {statusIcon(effectiveStatus)} {status}
                    </div>

                    {isRedeemed && (
                      <div className="rounded-3xl bg-slate-900 text-white p-5 shadow-lg">
                        <div className="font-black flex items-center gap-2 mb-2"><FaCheckCircle /> {tx('voucherUsed')}</div>
                        <p className="text-sm leading-6 text-white/80">
                          {tx('voucherUsedMessage')}{voucher.redeemed_at || redemption?.redeemed_at ? ` · ${tx('usedOn')} ${dateTime(voucher.redeemed_at || redemption?.redeemed_at)}` : ''}.
                          {redemption?.business_name ? ` ${tx('validationRecordedBy')} ${redemption.business_name}.` : ''}
                        </p>
                      </div>
                    )}

                    {isExpired && (
                      <div className="rounded-3xl bg-orange-50 border border-orange-100 p-5 text-orange-900">
                        <div className="font-black flex items-center gap-2 mb-2"><FaClock /> {tx('voucherExpired')}</div>
                        <p className="text-sm leading-6">
                          {tx('voucherExpiredMessage')}
                        </p>
                      </div>
                    )}

                    {isCancelled && (
                      <div className="rounded-3xl bg-red-50 border border-red-100 p-5 text-red-900">
                        <div className="font-black flex items-center gap-2 mb-2"><FaBan /> {tx('voucherCancelledTitle')}</div>
                        <p className="text-sm leading-6">
                          {tx('voucherCancelledMessage')}
                        </p>
                      </div>
                    )}

                    <div className="rounded-3xl border border-slate-100 bg-slate-50 p-5">
                      <div className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">{tx('summary')}</div>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between gap-3">
                          <span className="text-slate-500">{tx('value')}</span>
                          <strong className="text-slate-900">{moneyCad(voucher.amount_cents, { cents: true })}</strong>
                        </div>
                        <div className="flex justify-between gap-3">
                          <span className="text-slate-500">{tx('createdAt')}</span>
                          <strong className="text-slate-900">{dateOnly(voucher.created_at)}</strong>
                        </div>
                        <div className="flex justify-between gap-3">
                          <span className="text-slate-500">{tx('expiresOn')}</span>
                          <strong className="text-slate-900">{voucher.expires_at ? dateOnly(voucher.expires_at) : tx('noDateDefined')}</strong>
                        </div>
                        {isRedeemed && (
                          <>
                            <div className="flex justify-between gap-3">
                              <span className="text-slate-500">{tx('usedOn')}</span>
                              <strong className="text-slate-900 text-right">{dateTime(voucher.redeemed_at || redemption?.redeemed_at)}</strong>
                            </div>
                            <div className="flex justify-between gap-3">
                              <span className="text-slate-500">{tx('validatedBy')}</span>
                              <strong className="text-slate-900 text-right">{redemption?.business_name || tx('partnerBusiness')}</strong>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="rounded-3xl bg-blue-50 border border-blue-100 p-5 text-blue-900">
                      <div className="font-black flex items-center gap-2 mb-2"><FaShieldAlt /> {tx('forfeoSecurity')}</div>
                      <p className="text-sm leading-6">
                        {isActive
                          ? tx('voucherSecurityActive')
                          : tx('voucherSecurityInactive')}
                      </p>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                      <button
                        onClick={async () => {
                          try {
                            setPdfLoading(true);
                            await downloadVoucherPdf(voucher, language);
                          } finally {
                            setPdfLoading(false);
                          }
                        }}
                        disabled={pdfLoading}
                        className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-4 rounded-2xl font-black hover:bg-blue-700 disabled:opacity-60 disabled:cursor-wait"
                      >
                        <FaDownload /> {pdfLoading ? tx('preparingPdf') : tx('downloadVoucherPdf')}
                      </button>
                      <button onClick={() => window.print()} className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 text-white px-6 py-4 rounded-2xl font-black hover:bg-slate-800">
                        <FaPrint /> {tx('printVoucher')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default VoucherDetail;
